import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Bus, BusStateTypes } from "./Bus.util";

const name = "bus";
const extraActions = createExtraActions();

const BusInitialState: BusStateTypes = {
  createBus: ResetApiState(null),
  resetCreateBus: ResetApiState(null),
  editBus: ResetApiState(null),
  resetEditBus: ResetApiState(null),
  deleteBus: ResetApiState(null),
  resetDeleteBus: ResetApiState(null),
  buses: ResetApiState([]),
  pagedBuss: ResetApiState(ResetPaginationState()),
  bus: ResetApiState(null),
  resetBus: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createBus = createAsyncThunk<ResponseWrapper<Bus>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateBus = createAction(`/reset-create-${name}`);

  const editBus = createAsyncThunk<ResponseWrapper<Bus>, any>(
    `/edit-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .put(`/${name}/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditBus = createAction(`/reset-edit-${name}`);

  const deleteBus = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteBus = createAction(`/reset-delete-${name}`);

  const getBuses = createAsyncThunk<Bus[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedBuss = createAsyncThunk<PaginationType<Bus[]>, any>(
    `/get-paged-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/paged${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getBus = createAsyncThunk<Bus, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetBus = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createBus,
    resetCreateBus,
    editBus,
    resetEditBus,
    deleteBus,
    resetDeleteBus,
    getBuses,
    getPagedBuss,
    getBus,
    resetBus,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(builder: ActionReducerMapBuilder<BusStateTypes>) {
  return {
    ...createBusReducer(),
    ...resetCreateBus(),
    ...editBusReducer(),
    ...resetEditBus(),
    ...deleteBusReducer(),
    ...resetDeleteBus(),
    ...busesReducer(),
    ...pagedBussReducer(),
    ...busReducer(),
    ...resetBus(),
    ...resetStatesReducer(),
  };

  function createBusReducer() {
    return {
      ...builder.addCase(extraActions.createBus.pending, (state, action) => {
        state.createBus = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.createBus.fulfilled, (state, action) => {
        state.createBus = {
          loading: false,
          payload: action.payload.message,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.createBus.rejected, (state, action) => {
        state.createBus = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetCreateBus() {
    return {
      ...builder.addCase(extraActions.resetCreateBus, (state, action) => {
        state.resetCreateBus = ResetApiState(true);
        state.createBus = ResetApiState(null);
      }),
    };
  }

  function editBusReducer() {
    return {
      ...builder.addCase(extraActions.editBus.pending, (state, action) => {
        state.editBus = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editBus.fulfilled, (state, action) => {
        state.editBus = {
          loading: false,
          payload: action.payload.message,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editBus.rejected, (state, action) => {
        state.editBus = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetEditBus() {
    return {
      ...builder.addCase(extraActions.resetEditBus, (state, action) => {
        state.resetEditBus = ResetApiState(true);
        state.editBus = ResetApiState(null);
      }),
    };
  }

  function deleteBusReducer() {
    return {
      ...builder.addCase(extraActions.deleteBus.pending, (state, action) => {
        state.deleteBus = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.deleteBus.fulfilled, (state, action) => {
        state.deleteBus = {
          loading: false,
          payload: true,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.deleteBus.rejected, (state, action) => {
        state.deleteBus = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetDeleteBus() {
    return {
      ...builder.addCase(extraActions.resetDeleteBus, (state, action) => {
        state.resetDeleteBus = ResetApiState(true);
        state.deleteBus = ResetApiState(null);
      }),
    };
  }

  function busesReducer() {
    return {
      ...builder.addCase(extraActions.getBuses.pending, (state, action) => {
        state.buses = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getBuses.fulfilled, (state, action) => {
        state.buses = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getBuses.rejected, (state, action) => {
        state.buses = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function pagedBussReducer() {
    return {
      ...builder.addCase(extraActions.getPagedBuss.pending, (state, action) => {
        state.pagedBuss = {
          loading: true,
          payload: ResetPaginationState(),
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getPagedBuss.fulfilled,
        (state, action) => {
          state.pagedBuss = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedBuss.rejected,
        (state, action) => {
          state.pagedBuss = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function busReducer() {
    return {
      ...builder.addCase(extraActions.getBus.pending, (state, action) => {
        state.bus = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getBus.fulfilled, (state, action) => {
        state.bus = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getBus.rejected, (state, action) => {
        state.bus = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetBus() {
    return {
      ...builder.addCase(extraActions.resetBus, (state, action) => {
        state.resetBus = ResetApiState(true);
        state.bus = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createBus = ResetApiState(null);
        state.resetCreateBus = ResetApiState(null);
        state.editBus = ResetApiState(null);
        state.resetEditBus = ResetApiState(null);
        state.deleteBus = ResetApiState(null);
        state.resetDeleteBus = ResetApiState(null);
        state.buses = ResetApiState([]);
        state.bus = ResetApiState(null);
        state.resetBus = ResetApiState(null);
        state.resetBus = ResetApiState(null);
      }),
    };
  }
}

const BusSlice = createSlice({
  name,
  initialState: BusInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const BusActions = {
  ...BusSlice.actions,
  ...extraActions,
};
export const BusReducer = BusSlice.reducer;
